import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useGetShopCategoriesQuery } from "../features/shop/shopApiSlice";
import { useBecomeMarketerMutation, useCheckMarketerAvailabilityQuery } from "../features/user/userApiSlice";
import { useGetMarketerByUserIdQuery } from "../features/marketing/marketingApiSlice";
import { SuccessNotify } from "../constants/tostify";

export const marketingTypeMap = {
  Retailer: "retailerShop",
  Wholesaler: "wholesellerShop",
  Manufacturer: "manufacturerShop",
  "Raw Material": "rawMaterialShop",
  Homemade: "homemadeShop",
  Company: "company",
  "Services Profile": "services",
  "Transport Profile": "transportShop",
  "Farming Industry": "farmingShop",
  "Recycled Material Industry": "recycledShop",
  "Gems & Jewellery": "gemShop",
  "Used Items": "usedShop",
  "Used Vehicle": "usedVehicleShop",
  "Food & Restaurant": "foodShop",
  "Business Property": "property",
};

export const useBecomeAgent = () => {
  const navigate = useNavigate();
  const { name } = useSelector((state) => state.user);
  const authUser = useSelector((state) => state.auth?.user);
  const authToken =
    useSelector((state) => state.auth?.token) ||
    (typeof localStorage !== "undefined" ? localStorage.getItem("token") : null);
  // Persist rehydration can leave state.user.name empty briefly on refresh —
  // fall back to auth.user so the availability check still runs.
  const userId = name?._id || authUser?._id || null;

  const {
    data: availabilityData,
    isLoading: isAvailabilityLoading,
    isSuccess: isAvailabilitySuccess,
    isError: isAvailabilityError,
  } = useCheckMarketerAvailabilityQuery(userId, {
    skip: !userId,
    refetchOnMountOrArgChange: true,
  });

  // Secondary source of truth: existing MarketingAgent row for this user
  const {
    data: marketerRes,
    isLoading: isMarketerLoading,
    isSuccess: isMarketerSuccess,
    isError: isMarketerError,
  } = useGetMarketerByUserIdQuery(userId, {
    skip: !userId,
    refetchOnMountOrArgChange: true,
  });

  const hasMarketerRecord = useMemo(() => {
    const list = marketerRes?.data;
    return Array.isArray(list) && list.length > 0;
  }, [marketerRes]);

  // Optimistic: persisted user already flagged as marketer
  const persistedIsMarketer = Boolean(
    name?.isMarketer ||
      name?.IsMarketer ||
      authUser?.isMarketer ||
      authUser?.IsMarketer ||
      name?.marketerId ||
      authUser?.marketerId,
  );

  const isAlreadyAgent =
    persistedIsMarketer ||
    availabilityData?.available === false ||
    hasMarketerRecord;

  // Resolved once we know they are / are not an agent (do not use isFetching —
  // background refetch would hide the form for first-time applicants).
  const statusResolved =
    isAlreadyAgent ||
    availabilityData?.available === true ||
    (isAvailabilitySuccess && isMarketerSuccess && !hasMarketerRecord) ||
    (isAvailabilityError && isMarketerError);

  const isCheckingAgent =
    (!!authToken && !userId) ||
    (!!userId &&
      !statusResolved &&
      (isAvailabilityLoading ||
        isMarketerLoading ||
        (!isAvailabilitySuccess &&
          !isAvailabilityError &&
          !isMarketerSuccess &&
          !persistedIsMarketer)));

  useEffect(() => {
    if (!userId) return;
    if (isAlreadyAgent) {
      navigate("/marketing-dashboard", { replace: true });
    }
  }, [userId, isAlreadyAgent, navigate]);

  const [selectedIndustry, setSelectedIndustry] = useState("");
  const [error, setError] = useState("");
  const [apiType, setApiType] = useState("");

  const [selectshop, setSelectshop] = useState({ shopCategory: "" });

  const [marketingdata, setMarketingdata] = useState({
    MIndustryName: [
      {
        id: "",
        name: "",
      },
    ],
    MCategoryName: [
      {
        id: "",
        name: "",
      },
    ],
    MAddress: "",
    MCity: "",
    MCountry: "",
    MProvince: "",
  });

  // Fetch shop categories using RTK Query.
  // Transport: admin /transport-items stores leaf rows as transportShopItem
  // with non-empty parentCategory. Passing parentCategory:"" returns none —
  // omit parent filter so the dropdown matches admin data.
  const categoryQueryArgs = useMemo(() => {
    if (!apiType) return null;
    if (apiType === "transportShop") {
      return { type: "transportShopItem" };
    }
    return { type: apiType, parentCategory: "" };
  }, [apiType]);

  const { data: categoryData, isLoading: isCategoriesLoading } =
    useGetShopCategoriesQuery(categoryQueryArgs, {
      skip: !categoryQueryArgs,
      refetchOnMountOrArgChange: true,
    });

  const shopCategoryone = categoryData?.data?.Category || [];

  const [becomeMarketer, { isLoading: isSubmitting }] = useBecomeMarketerMutation();

  const Shopdatas = (e) => {
    setSelectshop((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value.split("."),
    }));
  };

  const categoryid = selectshop?.shopCategory[0];
  const categoryname = selectshop?.shopCategory[1];

  useEffect(() => {
    if (!categoryid || !categoryname) return;

    setMarketingdata((prev) => ({
      ...prev,
      MCategoryName: [
        {
          id: categoryid,
          name: categoryname,
        },
      ],
    }));
  }, [categoryid, categoryname]);

  const handleIndustryChange = (value) => {
    const mappedValue = marketingTypeMap[value];

    setSelectedIndustry(value);
    setApiType(mappedValue);
    setSelectshop({ shopCategory: "" }); // Reset category when industry changes

    setMarketingdata((prev) => ({
      ...prev,
      MIndustryName: [
        {
          id: "",
          name: mappedValue,
        },
      ],
    }));
  };

  const handleAddressChange = (value) => {
    setMarketingdata((prev) => ({
      ...prev,
      MAddress: value,
    }));
  };

  const handleCityChange = (value) => {
    setMarketingdata((prev) => ({
      ...prev,
      MCity: value,
    }));
  };

  const handleCountryChange = (value) => {
    setMarketingdata((prev) => ({
      ...prev,
      MCountry: value,
      MProvince: "",
      MCity: "",
    }));
  };

  const handleStateChange = (value) => {
    setMarketingdata((prev) => ({
      ...prev,
      MProvince: value,
      MCity: "",
    }));
  };

  const postMarketingData = async () => {
    setError(""); // Clear previous errors

    if (!selectedIndustry) {
      setError("Marketing industry is required.");
      return;
    }

    if (!selectshop.shopCategory || selectshop.shopCategory.length < 2 || !selectshop.shopCategory[0]) {
      setError("Marketing category is required.");
      return;
    }

    const finalPayload = {
      MAddress: marketingdata.MAddress,
      MCity: marketingdata.MCity,
      MCountry: marketingdata.MCountry,
      MProvince: marketingdata.MProvince,
      MIndustryName: selectedIndustry ? [
        {
          id: "",
          name: marketingTypeMap[selectedIndustry],
        },
      ] : [],
      MCategoryName: (selectshop.shopCategory && selectshop.shopCategory[0]) ? [
        {
          id: selectshop.shopCategory[0],
          name: selectshop.shopCategory[1],
        },
      ] : [],
    };

    try {
      const response = await becomeMarketer({
        id: userId,
        data: finalPayload,
      }).unwrap();

      if (response?.success) {
        SuccessNotify(response?.message || "User successfully registered as Marketing Person");
        navigate(`/marketing-dashboard`);
      } else {
        setError(response?.message || "Registration failed");
      }
    } catch (err) {
      console.error("Become marketer error:", err);
      setError(err?.data?.message || err?.message || "Registration failed");
    }
  };

  return {
    selectedIndustry,
    apiType,
    shopCategoryone,
    isCategoriesLoading,
    selectshop,
    marketingdata,
    isSubmitting,
    Shopdatas,
    handleIndustryChange,
    handleAddressChange,
    handleCityChange,
    handleCountryChange,
    handleStateChange,
    postMarketingData,
    error,
    setError,
    isCheckingAgent,
    isAlreadyAgent,
  };
};
